import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "en" | "es" | "fr" | "de" | "pt" | "zh";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    home: "Home",
    browse: "Browse Items",
    listItem: "List Your Item",
    about: "About",
    account: "Account",
    login: "Login",
    dashboard: "Dashboard",
    myBookings: "My Bookings",
    logout: "Log Out",
    heroTitle: "Rent Anything, Anytime",
    heroSubtitle: "From tools to tech, find what you need from people nearby",
    getStarted: "Get Started",
    browseItems: "Browse Items",
    categories: "Popular Categories",
    tools: "Tools",
    electronics: "Electronics",
    sports: "Sports",
    furniture: "Furniture",
    howItWorks: "How It Works",
    search: "Search",
    searchDesc: "Find what you need",
    book: "Book",
    bookDesc: "Request a rental",
    enjoy: "Enjoy",
    enjoyDesc: "Use and return",
    trust: "Why Choose Us",
    verified: "Verified Users",
    verifiedDesc: "All users are ID verified",
    secure: "Secure Payments",
    secureDesc: "Safe transactions",
    support: "24/7 Support",
    supportDesc: "We're here to help",
    quickLinks: "Quick Links",
    contact: "Contact",
    legal: "Legal",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    fees: "Fees",
    faq: "FAQ",
    contactUs: "Contact Us",
    allRights: "All rights reserved",
  },
  es: {
    home: "Inicio",
    browse: "Explorar Artículos",
    listItem: "Listar Tu Artículo",
    about: "Acerca de",
    account: "Cuenta",
    login: "Iniciar Sesión",
    dashboard: "Panel",
    myBookings: "Mis Reservas",
    logout: "Cerrar Sesión",
    heroTitle: "Alquila Cualquier Cosa, En Cualquier Momento",
    heroSubtitle: "Desde herramientas hasta tecnología, encuentra lo que necesitas de personas cercanas",
    getStarted: "Comenzar",
    browseItems: "Explorar Artículos",
    categories: "Categorías Populares",
    tools: "Herramientas",
    electronics: "Electrónicos",
    sports: "Deportes",
    furniture: "Muebles",
    howItWorks: "Cómo Funciona",
    search: "Buscar",
    searchDesc: "Encuentra lo que necesitas",
    book: "Reservar",
    bookDesc: "Solicita un alquiler",
    enjoy: "Disfrutar",
    enjoyDesc: "Usa y devuelve",
    trust: "Por Qué Elegirnos",
    verified: "Usuarios Verificados",
    verifiedDesc: "Todos los usuarios están verificados con ID",
    secure: "Pagos Seguros",
    secureDesc: "Transacciones seguras",
    support: "Soporte 24/7",
    supportDesc: "Estamos aquí para ayudar",
    quickLinks: "Enlaces Rápidos",
    contact: "Contacto",
    legal: "Legal",
    terms: "Términos de Servicio",
    privacy: "Política de Privacidad",
    fees: "Tarifas",
    faq: "Preguntas Frecuentes",
    contactUs: "Contáctanos",
    allRights: "Todos los derechos reservados",
  },
  fr: {
    home: "Accueil",
    browse: "Parcourir",
    listItem: "Publier un Article",
    about: "À propos",
    account: "Compte",
    login: "Connexion",
    dashboard: "Tableau de bord",
    myBookings: "Mes Réservations",
    logout: "Déconnexion",
    heroTitle: "Louez N'importe Quoi, N'importe Quand",
    heroSubtitle: "Des outils à la technologie, trouvez ce dont vous avez besoin près de chez vous",
    getStarted: "Commencer",
    browseItems: "Parcourir les Articles",
    categories: "Catégories Populaires",
    tools: "Outils",
    electronics: "Électronique",
    sports: "Sports",
    furniture: "Meubles",
    howItWorks: "Comment ça Marche",
    search: "Rechercher",
    searchDesc: "Trouvez ce dont vous avez besoin",
    book: "Réserver",
    bookDesc: "Demandez une location",
    enjoy: "Profitez",
    enjoyDesc: "Utilisez et retournez",
    trust: "Pourquoi Nous Choisir",
    verified: "Utilisateurs Vérifiés",
    verifiedDesc: "Tous les utilisateurs sont vérifiés",
    secure: "Paiements Sécurisés",
    secureDesc: "Transactions sécurisées",
    support: "Support 24/7",
    supportDesc: "Nous sommes là pour vous aider",
    quickLinks: "Liens Rapides",
    contact: "Contact",
    legal: "Légal",
    terms: "Conditions d'Utilisation",
    privacy: "Politique de Confidentialité",
    fees: "Tarifs",
    faq: "FAQ",
    contactUs: "Contactez-nous",
    allRights: "Tous droits réservés",
  },
  de: {
    home: "Startseite",
    browse: "Durchsuchen",
    listItem: "Artikel Einstellen",
    about: "Über uns",
    account: "Konto",
    login: "Anmelden",
    dashboard: "Dashboard",
    myBookings: "Meine Buchungen",
    logout: "Abmelden",
    heroTitle: "Miete Alles, Jederzeit",
    heroSubtitle: "Von Werkzeugen bis Technik, finde was du brauchst von Leuten in deiner Nähe",
    getStarted: "Loslegen",
    browseItems: "Artikel Durchsuchen",
    categories: "Beliebte Kategorien",
    tools: "Werkzeuge",
    electronics: "Elektronik",
    sports: "Sport",
    furniture: "Möbel",
    howItWorks: "So Funktioniert's",
    search: "Suchen",
    searchDesc: "Finde was du brauchst",
    book: "Buchen",
    bookDesc: "Miete anfragen",
    enjoy: "Genießen",
    enjoyDesc: "Nutzen und zurückgeben",
    trust: "Warum Uns Wählen",
    verified: "Verifizierte Nutzer",
    verifiedDesc: "Alle Nutzer sind ID-verifiziert",
    secure: "Sichere Zahlungen",
    secureDesc: "Sichere Transaktionen",
    support: "24/7 Support",
    supportDesc: "Wir sind für dich da",
    quickLinks: "Schnelllinks",
    contact: "Kontakt",
    legal: "Rechtliches",
    terms: "Nutzungsbedingungen",
    privacy: "Datenschutz",
    fees: "Gebühren",
    faq: "FAQ",
    contactUs: "Kontaktiere uns",
    allRights: "Alle Rechte vorbehalten",
  },
  pt: {
    home: "Início",
    browse: "Explorar",
    listItem: "Anunciar Item",
    about: "Sobre",
    account: "Conta",
    login: "Entrar",
    dashboard: "Painel",
    myBookings: "Minhas Reservas",
    logout: "Sair",
    heroTitle: "Alugue Qualquer Coisa, a Qualquer Hora",
    heroSubtitle: "De ferramentas a tecnologia, encontre o que precisa de pessoas próximas",
    getStarted: "Começar",
    browseItems: "Explorar Itens",
    categories: "Categorias Populares",
    tools: "Ferramentas",
    electronics: "Eletrônicos",
    sports: "Esportes",
    furniture: "Móveis",
    howItWorks: "Como Funciona",
    search: "Buscar",
    searchDesc: "Encontre o que precisa",
    book: "Reservar",
    bookDesc: "Solicite um aluguel",
    enjoy: "Aproveite",
    enjoyDesc: "Use e devolva",
    trust: "Por Que Nos Escolher",
    verified: "Usuários Verificados",
    verifiedDesc: "Todos os usuários são verificados",
    secure: "Pagamentos Seguros",
    secureDesc: "Transações seguras",
    support: "Suporte 24/7",
    supportDesc: "Estamos aqui para ajudar",
    quickLinks: "Links Rápidos",
    contact: "Contato",
    legal: "Legal",
    terms: "Termos de Serviço",
    privacy: "Política de Privacidade",
    fees: "Taxas",
    faq: "FAQ",
    contactUs: "Fale Conosco",
    allRights: "Todos os direitos reservados",
  },
  zh: {
    home: "首页",
    browse: "浏览物品",
    listItem: "发布物品",
    about: "关于我们",
    account: "账户",
    login: "登录",
    dashboard: "仪表板",
    myBookings: "我的预订",
    logout: "退出",
    heroTitle: "随时随地租借任何物品",
    heroSubtitle: "从工具到科技产品，找到您附近人们提供的一切",
    getStarted: "开始使用",
    browseItems: "浏览物品",
    categories: "热门分类",
    tools: "工具",
    electronics: "电子产品",
    sports: "运动器材",
    furniture: "家具",
    howItWorks: "使用方法",
    search: "搜索",
    searchDesc: "找到您需要的",
    book: "预订",
    bookDesc: "申请租借",
    enjoy: "享用",
    enjoyDesc: "使用并归还",
    trust: "为什么选择我们",
    verified: "已验证用户",
    verifiedDesc: "所有用户都已通过身份验证",
    secure: "安全支付",
    secureDesc: "安全交易",
    support: "全天候支持",
    supportDesc: "我们随时为您服务",
    quickLinks: "快速链接",
    contact: "联系方式",
    legal: "法律条款",
    terms: "服务条款",
    privacy: "隐私政策",
    fees: "费用",
    faq: "常见问题",
    contactUs: "联系我们",
    allRights: "版权所有",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
